import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, Paperclip, Send, Save, Bold, Italic, Underline, List, ListOrdered, Link2, Eraser, Loader2 } from 'lucide-react';
import { email, arquivoParaBase64, tamanho, escapar, limparHtml, dataHora, nomeDe } from './api';

const LIMITE_ANEXOS = 24 * 1024 * 1024;

/** Campo de destinatários: "chips" + sugestões dos contatos (Quality + quem já trocou e-mail). */
function CampoEnderecos({ rotulo, valor, onChange, contatos, autoFocus }) {
  const [texto, setTexto] = useState('');
  const [aberto, setAberto] = useState(false);
  const [marcado, setMarcado] = useState(0);
  const sugestoes = useMemo(() => {
    const t = texto.trim().toLowerCase();
    if (t.length < 2) return [];
    return contatos.filter(c => !valor.includes(c.email) && (c.email.includes(t) || (c.nome || '').toLowerCase().includes(t))).slice(0, 8);
  }, [texto, contatos, valor]);
  const adicionar = (e) => {
    const lista = String(e || '').split(/[;,\s]+/).map(s => s.trim().replace(/^<|>$/g, '')).filter(Boolean);
    if (lista.length) onChange([...new Set([...valor, ...lista])]);
    setTexto(''); setMarcado(0);
  };
  return (
    <div className="em-campo-end">
      <span className="em-rotulo">{rotulo}</span>
      <div className="em-chips">
        {valor.map(v => {
          const c = contatos.find(x => x.email === v);
          const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
          return (
            <span key={v} className={`em-chip ${ok ? '' : 'em-chip-erro'}`} title={v}>
              {c?.nome ? `${c.nome} <${v}>` : v}
              <button type="button" onClick={() => onChange(valor.filter(x => x !== v))}><X size={12} /></button>
            </span>
          );
        })}
        <input
          value={texto} autoFocus={autoFocus}
          onChange={e => { setTexto(e.target.value); setAberto(true); setMarcado(0); }}
          onFocus={() => setAberto(true)}
          onBlur={() => setTimeout(() => { setAberto(false); if (texto.trim()) adicionar(texto); }, 150)}
          onKeyDown={e => {
            if (e.key === 'ArrowDown') { e.preventDefault(); setMarcado(m => Math.min(m + 1, sugestoes.length - 1)); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setMarcado(m => Math.max(m - 1, 0)); }
            else if (['Enter', 'Tab', ',', ';'].includes(e.key) && (texto.trim() || sugestoes.length)) {
              if (e.key !== 'Tab' || texto.trim()) e.preventDefault();
              adicionar(sugestoes[marcado] && aberto ? sugestoes[marcado].email : texto);
            } else if (e.key === 'Backspace' && !texto && valor.length) onChange(valor.slice(0, -1));
          }}
          onPaste={e => { const t = e.clipboardData.getData('text'); if (/[;,\n]/.test(t)) { e.preventDefault(); adicionar(t); } }}
        />
      </div>
      {aberto && sugestoes.length > 0 && (
        <div className="em-sugestoes">
          {sugestoes.map((s, i) => (
            <button type="button" key={s.email} className={i === marcado ? 'ativa' : ''} onMouseDown={e => { e.preventDefault(); adicionar(s.email); }}>
              <strong>{s.nome || s.email}</strong> <span>{s.email}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const assinaturaHtml = a => (a ? `<div class="em-assinatura"><br>${escapar(a).replace(/\n/g, '<br>')}</div>` : '');
function citacao(orig) {
  const corpo = orig.html ? limparHtml(orig.html) : escapar(orig.texto).replace(/\n/g, '<br>');
  return `<br><div>Em ${escapar(dataHora(orig.data))}, ${escapar(orig.de?.map(nomeDe).join(', ') || '')} escreveu:</div>`
    + `<blockquote style="margin:0 0 0 .8ex;border-left:2px solid #ccc;padding-left:1ex">${corpo}</blockquote>`;
}
function encaminhado(orig) {
  const corpo = orig.html ? limparHtml(orig.html) : escapar(orig.texto).replace(/\n/g, '<br>');
  const lin = (r, v) => (v ? `<b>${r}:</b> ${escapar(v)}<br>` : '');
  return `<br><div>---------- Mensagem encaminhada ----------<br>${lin('De', orig.de?.map(p => `${p.nome} <${p.email}>`).join(', '))}${lin('Data', dataHora(orig.data))}`
    + `${lin('Assunto', orig.assunto)}${lin('Para', orig.para?.map(p => p.nome || p.email).join(', '))}${lin('Cc', orig.cc?.map(p => p.nome || p.email).join(', '))}</div><br>${corpo}`;
}
const prefixo = (p, assunto) => (new RegExp(`^${p}:`, 'i').test(assunto || '') ? assunto : `${p}: ${assunto || ''}`);

/**
 * Janela de escrever e-mail.
 * modo: 'novo' | 'responder' | 'responderTodos' | 'encaminhar' | 'rascunho'
 * original: e-mail aberto (resposta, encaminhar, rascunho), com contaId, pasta e uid.
 */
export default function Compor({ contas, contaId, modo = 'novo', original, inicial, onFechar, onEnviado }) {
  const [deConta, setDeConta] = useState(original?.contaId || contaId);
  const conta = contas.find(c => c.id === deConta) || contas[0];
  const [contatos, setContatos] = useState([]);
  const [para, setPara] = useState([]);
  const [cc, setCc] = useState([]);
  const [cco, setCco] = useState([]);
  const [verCopias, setVerCopias] = useState(false);
  const [assunto, setAssunto] = useState('');
  const [anexos, setAnexos] = useState([]);
  const [anexosOrig, setAnexosOrig] = useState([]);
  const [rascunho, setRascunho] = useState(null);
  const [enviando, setEnviando] = useState('');
  const [erro, setErro] = useState('');
  const [mexeu, setMexeu] = useState(false);
  const editor = useRef(null);
  const arquivo = useRef(null);

  useEffect(() => { email('contatos').then(r => setContatos(r.contatos || [])).catch(() => {}); }, []);

  // monta o e-mail inicial conforme o modo
  useEffect(() => {
    const eu = new Set(contas.map(c => c.endereco));
    const o = original || {};
    let html = '<p><br></p>' + assinaturaHtml(conta?.assinatura);
    if (modo === 'responder' || modo === 'responderTodos') {
      const resp = (o.responderPara?.length ? o.responderPara : o.de || []).map(p => p.email);
      setPara(resp.filter(e => !eu.has(e) || resp.length === 1));
      if (modo === 'responderTodos') {
        const outros = [...(o.para || []), ...(o.cc || [])].map(p => p.email).filter(e => !eu.has(e) && !resp.includes(e));
        if (outros.length) { setCc([...new Set(outros)]); setVerCopias(true); }
      }
      setAssunto(prefixo('Re', o.assunto));
      html += citacao(o);
    } else if (modo === 'encaminhar') {
      setAssunto(prefixo('Enc', o.assunto));
      setAnexosOrig((o.anexos || []).map(a => ({ ...a, marcado: true })));
      html += encaminhado(o);
    } else if (modo === 'rascunho') {
      setPara((o.para || []).map(p => p.email)); setCc((o.cc || []).map(p => p.email)); setCco((o.cco || []).map(p => p.email));
      if ((o.cc || []).length || (o.cco || []).length) setVerCopias(true);
      setAssunto(o.assunto === '(sem assunto)' ? '' : o.assunto || '');
      html = o.html ? limparHtml(o.html) : escapar(o.texto).replace(/\n/g, '<br>');
      setRascunho({ pasta: o.pasta, uid: o.uid });
    }
    if (inicial?.para) setPara(inicial.para);
    if (inicial?.assunto) setAssunto(inicial.assunto);
    if (editor.current) editor.current.innerHTML = html;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalAnexos = anexos.reduce((s, a) => s + a.tamanho, 0) + anexosOrig.filter(a => a.marcado).reduce((s, a) => s + (a.tamanho || 0), 0);

  async function anexar(lista) {
    const novos = [];
    for (const f of lista) {
      if (totalAnexos + novos.reduce((s, a) => s + a.tamanho, 0) + f.size > LIMITE_ANEXOS) { setErro('Os anexos passam de 24 MB — o servidor de e-mail recusaria.'); break; }
      novos.push({ nome: f.name, tipo: f.type || 'application/octet-stream', tamanho: f.size, base64: await arquivoParaBase64(f) });
    }
    setAnexos(a => [...a, ...novos]);
    setMexeu(true);
  }

  const cmd = (c, v) => { editor.current?.focus(); document.execCommand(c, false, v); setMexeu(true); };

  function dados() {
    const html = editor.current?.innerHTML || '';
    return {
      contaId: deConta, para, cc, cco, assunto, html, texto: editor.current?.innerText || '',
      anexos: anexos.map(({ nome, tipo, base64 }) => ({ nome, tipo, base64 })),
      ...(modo === 'responder' || modo === 'responderTodos' ? { resposta: { pasta: original.pasta, uid: original.uid } } : {}),
      ...(modo === 'encaminhar' ? { encaminhar: { pasta: original.pasta, uid: original.uid, anexos: anexosOrig.filter(a => a.marcado).map(a => a.indice) } } : {}),
      ...(rascunho ? { rascunho } : {}),
    };
  }

  async function enviar() {
    setErro('');
    if (!para.length && !cc.length && !cco.length) return setErro('Informe pelo menos um destinatário.');
    const ruins = [...para, ...cc, ...cco].filter(e => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
    if (ruins.length) return setErro(`Endereço inválido: ${ruins.join(', ')}`);
    if (!assunto.trim() && !window.confirm('Enviar sem assunto?')) return;
    setEnviando('enviar');
    try {
      await email('enviar', dados());
      onEnviado?.();
      onFechar();
    } catch (e) { setErro(e.message); } finally { setEnviando(''); }
  }
  async function salvarRascunho() {
    setErro(''); setEnviando('rascunho');
    try {
      const r = await email('rascunho', dados());
      setRascunho(r.rascunho); setMexeu(false);
    } catch (e) { setErro(e.message); } finally { setEnviando(''); }
  }
  function fechar() {
    if (mexeu && !window.confirm('Fechar sem enviar? O que você escreveu será perdido (use "Salvar rascunho" para guardar).')) return;
    onFechar();
  }

  return (
    <div className="em-modal-fundo" onMouseDown={e => { if (e.target === e.currentTarget) fechar(); }}>
      <div className="em-compor" onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); anexar([...e.dataTransfer.files]); }}>
        <div className="em-compor-cab">
          <strong>{{ novo: 'Novo e-mail', responder: 'Responder', responderTodos: 'Responder a todos', encaminhar: 'Encaminhar', rascunho: 'Rascunho' }[modo]}</strong>
          <button className="em-icone" onClick={fechar} title="Fechar"><X size={18} /></button>
        </div>
        <div className="em-compor-campos">
          {contas.length > 1 ? (
            <div className="em-campo-end">
              <span className="em-rotulo">De</span>
              <select value={deConta} onChange={e => setDeConta(e.target.value)} disabled={modo !== 'novo'}>
                {contas.map(c => <option key={c.id} value={c.id}>{c.nome} &lt;{c.endereco}&gt;</option>)}
              </select>
            </div>
          ) : (
            <div className="em-campo-end"><span className="em-rotulo">De</span><span className="em-de-fixo">{conta?.nome} &lt;{conta?.endereco}&gt;</span></div>
          )}
          <CampoEnderecos rotulo="Para" valor={para} onChange={v => { setPara(v); setMexeu(true); }} contatos={contatos} autoFocus={modo === 'novo' || modo === 'encaminhar'} />
          {!verCopias && <button type="button" className="em-link-copias" onClick={() => setVerCopias(true)}>Cc / Cco</button>}
          {verCopias && <>
            <CampoEnderecos rotulo="Cc" valor={cc} onChange={v => { setCc(v); setMexeu(true); }} contatos={contatos} />
            <CampoEnderecos rotulo="Cco" valor={cco} onChange={v => { setCco(v); setMexeu(true); }} contatos={contatos} />
          </>}
          <div className="em-campo-end">
            <span className="em-rotulo">Assunto</span>
            <input className="em-assunto" value={assunto} onChange={e => { setAssunto(e.target.value); setMexeu(true); }} />
          </div>
        </div>
        <div className="em-barra-editor">
          <button type="button" onClick={() => cmd('bold')} title="Negrito"><Bold size={15} /></button>
          <button type="button" onClick={() => cmd('italic')} title="Itálico"><Italic size={15} /></button>
          <button type="button" onClick={() => cmd('underline')} title="Sublinhado"><Underline size={15} /></button>
          <button type="button" onClick={() => cmd('insertUnorderedList')} title="Lista"><List size={15} /></button>
          <button type="button" onClick={() => cmd('insertOrderedList')} title="Lista numerada"><ListOrdered size={15} /></button>
          <button type="button" onClick={() => { const u = window.prompt('Endereço do link (https://…)'); if (u && /^(https?:|mailto:)/i.test(u)) cmd('createLink', u); }} title="Link"><Link2 size={15} /></button>
          <button type="button" onClick={() => cmd('removeFormat')} title="Limpar formatação"><Eraser size={15} /></button>
          <span className="em-sep" />
          <button type="button" onClick={() => arquivo.current?.click()} title="Anexar arquivo"><Paperclip size={15} /> Anexar</button>
          <input ref={arquivo} type="file" multiple hidden onChange={e => { anexar([...e.target.files]); e.target.value = ''; }} />
        </div>
        <div ref={editor} className="em-editor" contentEditable suppressContentEditableWarning onInput={() => setMexeu(true)}
          onPaste={e => {
            // colar de outro lugar: mantém o texto/formatação simples, sem scripts nem estilos da página de origem
            const html = e.clipboardData.getData('text/html');
            if (html) { e.preventDefault(); document.execCommand('insertHTML', false, limparHtml(html)); }
          }} />
        {(anexos.length > 0 || anexosOrig.length > 0) && (
          <div className="em-anexos-compor">
            {anexosOrig.map(a => (
              <label key={`o${a.indice}`} className={`em-anexo ${a.marcado ? '' : 'em-anexo-off'}`}>
                <input type="checkbox" checked={a.marcado} onChange={() => setAnexosOrig(l => l.map(x => x.indice === a.indice ? { ...x, marcado: !x.marcado } : x))} />
                <Paperclip size={13} /> {a.nome} <span>{tamanho(a.tamanho)}</span>
              </label>
            ))}
            {anexos.map((a, i) => (
              <span key={`n${i}`} className="em-anexo">
                <Paperclip size={13} /> {a.nome} <span>{tamanho(a.tamanho)}</span>
                <button type="button" onClick={() => setAnexos(l => l.filter((_, j) => j !== i))}><X size={12} /></button>
              </span>
            ))}
            <span className="em-sub">Total {tamanho(totalAnexos)} de 24 MB</span>
          </div>
        )}
        {erro && <div className="em-aviso em-aviso-erro">{erro}</div>}
        <div className="em-compor-rodape">
          <button className="em-btn em-btn-primario" onClick={enviar} disabled={!!enviando}>
            {enviando === 'enviar' ? <Loader2 size={15} className="em-gira" /> : <Send size={15} />} Enviar
          </button>
          <button className="em-btn" onClick={salvarRascunho} disabled={!!enviando}>
            {enviando === 'rascunho' ? <Loader2 size={15} className="em-gira" /> : <Save size={15} />} Salvar rascunho
          </button>
          {rascunho && !mexeu && <span className="em-sub">Rascunho salvo</span>}
          <span className="em-sub em-dica">Arraste arquivos para cá para anexar</span>
        </div>
      </div>
    </div>
  );
}
